import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  ListItemText,
  ListItem,
  List,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  DesktopTower,
  FileText,
  Globe,
  ForkKnife,
  CaretRight,
  CheckCircle,
  CaretLeft,
} from '@phosphor-icons/react';

import { RootState } from '../../utils/redux/redux';
import {
  updateMenuConfig,
  setStepStatus,
  setErrors,
  clearErrors,
  setIsAccessRequested,
  setIsMenuGenerationStarted,
} from '../../utils/redux/slices/userJourneySlice';
import { menuConfigurationSchema } from '../../schemas/userJourney/menuConfiguration';
import { ValidationError } from 'yup';
import AccessRequestSuccessModal from '../modals/RequestAccessModal';
import { useLocation, useNavigate } from 'react-router';
import { useStores } from './FirstUserJourney/useStores';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  connectBusinessApi,
  getPosConfigApi,
  getTmBillBrandInfoApi,
  getTmBillMenuApi,
} from '../../utils/apis';
import { useUser } from '../../hooks/useUser';
import UnderDevelopment from '../common/UnderDevelopement';
import TMBillBrandInfoModal from '../modals/TMBillBrandInfoModal';
import MessagePopup from '../common/MessagePopup';
import { useAlert } from '../../hooks/useAlert';

const MenuConfiguration: FC<{
  selectedSubSection?: string;
  onNext?: () => void;
  handleGetStarted?: () => void;
  isPending: boolean;
  handleGetBack?: () => void;
}> = ({ selectedSubSection, onNext, handleGetStarted, isPending }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { menuConfig, errors } = useSelector(
    (state: RootState) => state.userJourney as { menuConfig: any; errors: any }
  );
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showBrandInfoModal, setShowBrandInfoModal] = useState(false);
  const [selectedStoreInfo, setSelectedStoreInfo] = useState({
    outletName: '',
    brandName: '',
    address: '',
    tmBillBrandShortName: '',
    storeId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const { user } = useUser();
  const { storesList } = useStores();
  const [isBrandInfoLoading, setIsBrandInfoLoading] = useState(false);
  const { alert, setAlert, showAlert } = useAlert();

  const { mutateAsync: connectBusiness, isPending: isConnectingBusiness } =
    useMutation({
      mutationFn: (businessId: string) => connectBusinessApi(businessId),
      onSuccess: () => {
        dispatch(setIsAccessRequested(true));
        setShowAccessModal(true);
      },
      onError: () => {
        console.log('Business connection failed');
      },
    });

  const { data: posConfig } = useQuery({
    queryKey: ['pos-config'],
    queryFn: getPosConfigApi,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  const handleMethodSelect = (method: string) => {
    dispatch(updateMenuConfig({ menuSettingMethod: method }));
    if (errors.menuSettingMethod) {
      dispatch(setErrors({ ...errors, menuSettingMethod: '' }));
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(updateMenuConfig({ [name]: value }));

    // If environment changes, update the posConfig data in Redux
    if (name === 'environment') {
      const selectedPos = posConfig?.data?.data?.find(
        (pos: any) => pos.posName === menuConfig.data.posName
      );

      if (selectedPos?.pos_config?.[0]?.[value]?.[0]) {
        const envConfig = selectedPos.pos_config[0][value][0];

        // Update Redux with the apikey and endpoint for the selected environment
        dispatch(
          updateMenuConfig({
            posConfig: {
              apikey: envConfig.apikey,
              endpoint: envConfig.endpoint,
            },
          })
        );
      }
    }

    // If posName changes, reset environment-specific data and select the first available environment
    if (name === 'posName') {
      const selectedPos = posConfig?.data?.data?.find(
        (pos: any) => pos.posName === value
      );

      if (selectedPos?.pos_config?.[0]) {
        const environments = Object.keys(selectedPos.pos_config[0]);
        const defaultEnv = environments[0] || 'stage';
        const envConfig = selectedPos.pos_config[0][defaultEnv][0];

        // Update Redux with the default environment and its config
        dispatch(
          updateMenuConfig({
            environment: defaultEnv,
            posConfig: {
              apikey: envConfig.apikey,
              endpoint: envConfig.endpoint,
            },
          })
        );
      }
    }

    if (errors[name]) {
      dispatch(setErrors({ ...errors, [name]: '' }));
    }
  };

  const handleRequestAccess = () => {
    connectBusiness(menuConfig.data.businessId);
  };

  const handleCloseAccessModal = () => {
    setShowAccessModal(false);
  };

  useEffect(() => {
    if (user?.business?.isRequestSend) {
      dispatch(setIsAccessRequested(true));
    }
  }, [user?.business?.isRequestSend, dispatch]);

  useEffect(() => {
    // Map API menuSettingMethod to UI method
    const methodMapping: Record<string, string> = {
      pos_integration: 'pos',
      web_scrapping: 'web',
      file_upload: 'file',
      manual_cataloging: 'manual',
    };

    // Get the first store's method and POS details
    const store = storesList;
    if (
      store &&
      !menuConfig.data.menuSettingMethod &&
      store.menuSettingMethod
    ) {
      const uiMethod = methodMapping[store.menuSettingMethod];
      dispatch(
        updateMenuConfig({
          menuSettingMethod: uiMethod,
          posName:
            store.menuSettingDetails?.posName === 'Urban Piper'
              ? 'Urban Piper'
              : '',
          businessId: store.menuSettingDetails?.businessId || '',
        })
      );
    }
  }, [storesList, dispatch, menuConfig.data.menuSettingMethod]);

  useEffect(() => {
    if (storesList && menuConfig.data.menuSettingMethod === 'pos') {
      const menuSettingDetails = storesList.reduce(
        (acc: any, store: any, index: number) => {
          const storeId = store.id || store.storeId; // Fallback if id is named differently
          const refId = store.menuSettingDetails?.refId || '';

          if (!storeId) {
            console.warn(`Store at index ${index} is missing an ID`, store);
            return acc; // Skip if no valid ID
          }

          acc[storeId] = {
            storeId,
            refId,
          };

          return acc;
        },
        {}
      );

      dispatch(updateMenuConfig({ menuSettingDetails }));
      dispatch(
        setStepStatus({
          step: 'menuConfig',
          status: { isComplete: true, hasError: false },
        })
      );
    }
  }, [menuConfig.data.menuSettingMethod, dispatch]);

  const handlePreviewInfo = async (storeId: string) => {
    const brandShortName =
      menuConfig.data.menuSettingDetails?.[storeId]?.tmBillBrandShortName;
    if (brandShortName) {
      try {
        setIsBrandInfoLoading(true);
        setShowBrandInfoModal(true);

        const response = await getTmBillBrandInfoApi(
          user?.tenantName,
          brandShortName
        );

        // Check if response.data has the expected structure
        if (response.data) {
          const brandData = response.data;
          // Get the first outlet data (assuming there's at least one)
          const outletData =
            brandData.outlets && brandData.outlets.length > 0
              ? brandData.outlets[0]
              : { name: '', address: '' };

          setSelectedStoreInfo({
            outletName: outletData.name || ' ',
            brandName: brandData.brand_name || ' ',
            address: outletData.address || ' ',
            tmBillBrandShortName: brandData.brand_short_name || '',
            storeId: storeId || '',
          });
        }
      } catch (error) {
        console.error('Error fetching brand info:', error);
      } finally {
        setIsBrandInfoLoading(false);
      }
    }
  };

  const handleMenuGeneration = () => {
    try {
      setIsSubmitting(true);

      // Start the API call but don't wait for its completion
      getTmBillMenuApi(
        user?.tenantName,
        selectedStoreInfo.tmBillBrandShortName,
        selectedStoreInfo.storeId
      )
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.error('Error generating TM Bill menu:', error);
        });

      // Close the modal and show success message after 3 seconds
      setTimeout(() => {
        setShowBrandInfoModal(false);
        setIsSubmitting(false);
        dispatch(setIsMenuGenerationStarted(true));
        showAlert(
          'Your menu will show up in 2-3 minutes in knowledge base',
          'success'
        );
      }, 3000);
    } catch (error) {
      console.error('Error starting TM Bill menu generation:', error);
      showAlert('Failed to generate TM Bill menu. Please try again.', 'error');
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    try {
      // First validate the data
      await menuConfigurationSchema.validate(menuConfig.data, {
        abortEarly: false,
      });

      // Transform menuSettingDetails
      const menuSettingDetailsArray =
        storesList?.map((store: any) => ({
          storeId: store.id,
          refId:
            menuConfig.data.posName === 'Urban Piper'
              ? menuConfig.data.menuSettingDetails?.[store.id]?.refId || ''
              : '',
          tmBillBrandShortName:
            menuConfig.data.posName === 'tmbill'
              ? menuConfig.data.menuSettingDetails?.[store.id]
                  ?.tmBillBrandShortName || ''
              : '',
        })) || [];

      // Create the formatted menu config
      const formattedMenuConfig = {
        menuSettingMethod:
          menuConfig.data.menuSettingMethod === 'pos'
            ? 'pos_integration'
            : menuConfig.data.menuSettingMethod === 'web'
              ? 'web_scrapping'
              : menuConfig.data.menuSettingMethod === 'file'
                ? 'file_upload'
                : 'manual_cataloging',
        menuSettingDetails: menuSettingDetailsArray,
        ...(menuConfig.data.posName === 'Urban Piper' && {
          businessId: menuConfig.data.businessId,
        }),
        ...(menuConfig.data.posName === 'tmbill' && {
          tmbillposId: menuConfig.data.tmbillposId,
        }),
      };

      // Update Redux with formatted config
      dispatch(updateMenuConfig(formattedMenuConfig));
      dispatch(
        setStepStatus({
          step: 'menuConfig',
          status: { isComplete: true, hasError: false },
        })
      );
      dispatch(clearErrors());

      // Call onNext to save the data
      if (onNext) {
        onNext();
      }

      // Continue directly to agent definition (remove extra pre-agent step)
      handleGetStarted?.();
    } catch (err: any) {
      if (err instanceof ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach((error: any) => {
          newErrors[error.path as string] = error.message;
        });
        dispatch(setErrors(newErrors));
        dispatch(
          setStepStatus({
            step: 'menuConfig',
            status: { isComplete: false, hasError: true },
          })
        );
      }
    }
  };

  const methods = [
    {
      id: 'pos',
      title: 'POS Integration',
      description: 'Sync with your existing system for real-time updates',
      icon: <DesktopTower size={24} weight="duotone" />,
      highlight: (
        <Chip
          label="Fastest"
          size="small"
          sx={{
            '& .MuiChip-label': {
              fontSize: '10px',
              color: 'custom.textBlue',
              fontWeight: 'bold',
            },
          }}
        />
      ),
    },
    {
      id: 'file',
      title: 'File Upload',
      description: 'Upload a CSV or spreadsheet to import your menu',
      icon: <FileText size={24} weight="duotone" />,
    },
    {
      id: 'web',
      title: 'Web Scrapping',
      description: 'Fetch menu details directly from your website',
      icon: <Globe size={24} weight="duotone" />,
    },
    {
      id: 'manual',
      title: 'Manual Cataloging',
      description: 'Enter items manually for full control over menu items',
      icon: <ForkKnife size={24} weight="duotone" />,
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'white',
        '&::-webkit-scrollbar': { display: 'none' },
        overflowY: 'auto',
      }}
    >
      {isPending ? (
        <Skeleton variant="rectangular" width="100%" height={40} />
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{
            mt: 3,
            py: location.pathname.includes('/knowledge-base') ? '23px' : 0,
          }}
        >
          Choose how you'd like to add your menu to Quibble AI
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px',
          m: 2,
          justifyContent: 'center',
          py: 1,
        }}
      >
        {isPending ? (
          <>
            {/* Skeleton Boxes */}
            {[...Array(6)].map((_, index) => (
              <Box key={index} mb={2}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={76}
                  sx={{ borderRadius: '12px' }}
                />
              </Box>
            ))}
            {/* Additional Content Lines */}
            {[...Array(3)].map((_, index) => (
              <Skeleton
                key={index + 10}
                variant="rectangular"
                width="100%"
                height={10}
                sx={{ borderRadius: '12px', mt: 1 }}
              />
            ))}
          </>
        ) : (
          methods.map(method => (
            <Box
              key={method.id}
              onClick={() => handleMethodSelect(method.id)}
              sx={{
                width: '100%',
                height: '76px',
                p: '14px',
                border: '1px solid',
                borderColor:
                  menuConfig.data.menuSettingMethod === method.id ||
                  selectedSubSection === method.id
                    ? 'primary.main'
                    : 'grey.300',
                borderRadius: '12px',
                cursor: 'pointer',
                position: 'relative',
                bgcolor: 'background.paper',
                '&:hover': { borderColor: 'primary.main' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {method.highlight && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'primary.main',
                    fontSize: '12px',
                  }}
                >
                  {method.highlight}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    color: 'primary.main',
                    bgcolor: '#F4F7FF',
                    p: 0.5,
                    height: '44px',
                    width: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {method.icon}
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontSize: '14px' }}
                  >
                    {method.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '12px', fontWeight: 400 }}
                  >
                    {method.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: 'flex',
          gap: '30px',
          px: 2,
          justifyContent: 'center',
          flexDirection: 'row',
          alignItems: 'flex-start',
          overflow: 'visible',
          mt: 2,
        }}
      >
        <Box
          sx={{
            width: '60%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {menuConfig.data.menuSettingMethod === 'pos' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  borderRadius: '8px',
                  p: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Select your POS
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    name="posName"
                    value={menuConfig.data.posName || ''}
                    onChange={handleChange}
                    placeholder="Placeholder"
                    size="small"
                    error={!!errors.posName}
                    helperText={errors.posName}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        height: '40px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                      },
                    }}
                  >
                    {posConfig?.data?.data?.map((pos: any) => (
                      <MenuItem key={pos._id} value={pos.posName}>
                        {pos.posName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {menuConfig.data.posName === 'Urban Piper' && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: '120px', flexShrink: 0 }}
                      >
                        Environment
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        name="environment"
                        value={menuConfig.data.environment || 'stage'}
                        onChange={handleChange}
                        size="small"
                        error={!!errors.environment}
                        helperText={errors.environment}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          },
                        }}
                      >
                        {posConfig?.data?.data?.find(
                          (pos: any) => pos.posName === menuConfig.data.posName
                        )?.pos_config[0] &&
                          Object.keys(
                            posConfig.data.data.find(
                              (pos: any) =>
                                pos.posName === menuConfig.data.posName
                            ).pos_config[0]
                          ).map((env: string) => (
                            <MenuItem key={env} value={env}>
                              {env.charAt(0).toUpperCase() + env.slice(1)}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: '120px', flexShrink: 0 }}
                      >
                        Business ID
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            fullWidth
                            placeholder="Enter your POS Business ID"
                            name="businessId"
                            size="small"
                            value={menuConfig.data.businessId}
                            onChange={handleChange}
                            error={!!errors.businessId}
                            helperText={errors.businessId}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                fontSize: '12px',
                              },
                              '& > div': {
                                height: '40px',
                              },
                            }}
                          />
                          <Button
                            variant="outlined"
                            color="primary"
                            disabled={
                              menuConfig.data.businessId === '' ||
                              isConnectingBusiness
                            }
                            onClick={handleRequestAccess}
                            sx={{
                              minWidth: 136,
                              height: 40,
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontSize: '14px',
                              boxShadow: 'none',
                              whiteSpace: 'nowrap',
                              ...(menuConfig.isAccessRequested && {
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'rgba(55, 183, 125, 1)',
                                color: 'rgba(55, 183, 125, 1)',
                                fontSize: '14px',
                                pointerEvents: 'none',
                                '&:hover': {
                                  bgcolor: 'white',
                                },
                              }),
                            }}
                          >
                            {menuConfig.isAccessRequested ? (
                              <>
                                <CheckCircle
                                  size={16}
                                  weight="fill"
                                  color="rgba(55, 183, 125, 1)"
                                  style={{ marginRight: 2 }}
                                />
                                Request Sent
                              </>
                            ) : isConnectingBusiness ? (
                              'Requesting...'
                            ) : (
                              'Request Access'
                            )}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                    {menuConfig.isAccessRequested && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 2,
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            width: '140px',
                          }}
                        >
                          Location ID
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            flex: 1,
                          }}
                        >
                          {storesList?.map((store: any) => (
                            <Box
                              key={store._id}
                              sx={{
                                display: 'flex',
                                gap: 2,
                              }}
                            >
                              <TextField
                                placeholder="Store Alias"
                                size="small"
                                value={store.friendly_name}
                                disabled
                                sx={{
                                  width: '200px',
                                  fontSize: '12px',
                                  bgcolor: 'rgb(243, 244, 246)',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '& input': {
                                      fontSize: '14px',
                                    },
                                  },
                                }}
                              />
                              <TextField
                                placeholder="ref ID"
                                size="small"
                                name={`locationId.${store.id}`}
                                value={
                                  menuConfig.data.menuSettingDetails?.[store.id]
                                    ?.refId || ''
                                }
                                onChange={e => {
                                  dispatch(
                                    updateMenuConfig({
                                      menuSettingDetails: {
                                        ...menuConfig.data.menuSettingDetails,
                                        [store.id]: {
                                          storeId: store.id,
                                          refId: e.target.value,
                                        },
                                      },
                                    })
                                  );
                                }}
                                error={!!errors[`locationId.${store.id}`]}
                                helperText={errors[`locationId.${store.id}`]}
                                sx={{
                                  width: '200px',
                                  '& input': {
                                    fontSize: '14px',
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                  },
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                {menuConfig.data.posName === 'tmbill' && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        TM POS ID
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: { xs: 'wrap', md: 'nowrap' },
                          width: '100%',
                        }}
                      >
                        <TextField
                          fullWidth
                          placeholder="Enter your TM POS ID"
                          name="tmbillposId"
                          size="small"
                          value={menuConfig.data.tmbillposId}
                          onChange={handleChange}
                          error={!!errors.tmbillposId}
                          helperText={errors.tmbillposId}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              height: '40px',
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 12px',
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    {menuConfig.data.tmbillposId !== '' && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Brand Short Name
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '12px', mb: 2 }}
                        >
                          Enter Brand Short Name for the respective store.
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {storesList?.map((store: any) => (
                            <Box
                              key={store.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: { xs: 'wrap', md: 'nowrap' },
                              }}
                            >
                              <TextField
                                placeholder="Store Alias"
                                size="small"
                                value={store?.friendly_name}
                                disabled
                                sx={{
                                  width: '35%',
                                  flexShrink: 0,
                                  fontSize: '12px',
                                  bgcolor: 'rgb(243, 244, 246)',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    '& input': {
                                      fontSize: '14px',
                                      padding: '8px 12px',
                                    },
                                  },
                                }}
                              />
                              <TextField
                                placeholder="brand short name"
                                size="small"
                                name={`tmBillBrandShortName.${store.id}`}
                                value={
                                  menuConfig.data.menuSettingDetails?.[store.id]
                                    ?.tmBillBrandShortName || ''
                                }
                                onChange={e => {
                                  dispatch(
                                    updateMenuConfig({
                                      menuSettingDetails: {
                                        ...menuConfig.data.menuSettingDetails,
                                        [store.id]: {
                                          storeId: store.id,
                                          tmBillBrandShortName: e.target.value,
                                        },
                                      },
                                    })
                                  );
                                }}
                                error={
                                  !!errors[`tmBillBrandShortName.${store.id}`]
                                }
                                helperText={
                                  errors[`tmBillBrandShortName.${store.id}`]
                                }
                                sx={{
                                  width: '40%',
                                  flexShrink: 0,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    '& input': {
                                      fontSize: '14px',
                                      padding: '8px 12px',
                                    },
                                  },
                                }}
                              />
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handlePreviewInfo(store.id)}
                                disabled={
                                  !menuConfig.data.menuSettingDetails?.[
                                    store.id
                                  ]?.tmBillBrandShortName
                                }
                              >
                                preview info
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
        {menuConfig.data.menuSettingMethod === 'pos' && (
          <Box
            sx={{
              width: '40%',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'lightGray.main',
                borderRadius: '8px',
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
              }}
            >
              <Typography
                color="text.primary"
                fontWeight="500"
                sx={{ fontSize: '16px', mb: 1 }}
              >
                How to Connect Your POS with Quibble AI
              </Typography>
              <List sx={{ pt: 0, px: 0 }}>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '500',
                        color: 'text.primary',
                        fontSize: '14px',
                      }}
                    >
                      1. Enter Your Business ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '12px' }}
                    >
                      Provide your unique Business ID to initiate the POS
                      integration.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '500',
                        color: 'text.primary',
                        fontSize: '14px',
                      }}
                    >
                      2. Request Integration Access
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '12px' }}
                    >
                      Click on "Request Access" to initiate the integration
                      approval process with your POS system.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '500',
                        color: 'text.primary',
                        fontSize: '14px',
                      }}
                    >
                      3. Enter Your Location ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '12px' }}
                    >
                      Use the Location ID from POS to accurately link your
                      business location.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '500',
                        color: 'text.primary',
                        fontSize: '14px',
                      }}
                    >
                      4. Proceed to the Next Step
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '12px' }}
                    >
                      While your POS integration request is under review, you
                      can continue setting up your AI assistant.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem sx={{ py: 0.25, px: 0 }}>
                  <ListItemText>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '500',
                        color: 'text.primary',
                        fontSize: '14px',
                      }}
                    >
                      5. Publish Your Menu
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '12px' }}
                    >
                      Once integration is approved, go to POS, select the
                      desired locations, and click the "Publish" button to make
                      your menu live.
                    </Typography>
                  </ListItemText>
                </ListItem>
              </List>
            </Box>
          </Box>
        )}
      </Box>

      {menuConfig.data.menuSettingMethod === 'web' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 2,
            // width: '60%',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: '30%' }}
            >
              Enter your website link
            </Typography>
            <Box sx={{ width: '50%' }}>
              <TextField
                disabled
                fullWidth
                placeholder="www.yourwebsite.com"
                value={menuConfig.data.websiteUrl}
                onChange={e => {
                  dispatch(updateMenuConfig({ websiteUrl: e.target.value }));
                }}
                error={!!errors.websiteUrl}
                helperText={errors.websiteUrl}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
          </Box>
          <UnderDevelopment />
        </Box>
      )}
      {menuConfig.data.menuSettingMethod === 'file' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 2,
            // width: '60%',
            mb: 2,
          }}
        >
          <UnderDevelopment />
        </Box>
      )}
      {menuConfig.data.menuSettingMethod === 'manual' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 2,
            // width: '60%',
            mb: 2,
          }}
        >
          <UnderDevelopment />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          p: 1,
          borderTop: '1px solid',
          borderColor: 'lightGray.main',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CaretLeft size={16} />}
          sx={{
            height: '34px',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            color: 'custom.textBlue',
            backgroundColor: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
          onClick={() => navigate('/add-new-assistant')}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          endIcon={<CaretRight size={16} />}
          disabled={
            isPending ||
            (menuConfig.data.posName === 'Urban Piper' &&
              !menuConfig.data.businessId) ||
            (menuConfig.data.posName === 'tmbill' &&
              !Object.values(menuConfig.data.menuSettingDetails || {}).some(
                (detail: any) => detail.tmBillBrandShortName
              ))
          }
          sx={{
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
          onClick={handleNext}
        >
          {isPending ? 'Saving...' : 'Next'}
        </Button>
      </Box>

      <AccessRequestSuccessModal
        open={showAccessModal}
        onClose={handleCloseAccessModal}
      />
      <TMBillBrandInfoModal
        open={showBrandInfoModal}
        onClose={() => setShowBrandInfoModal(false)}
        storeInfo={selectedStoreInfo}
        isLoading={isBrandInfoLoading}
        handleMenuGeneration={handleMenuGeneration}
        isSubmitting={isSubmitting}
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default MenuConfiguration;
